import React from "react";
import { createPortal } from "react-dom";
import { MIN_WORKSPACE_USER_SEARCH_LENGTH } from "../../../hooks/useWorkspaceUserSearch";
import { MemberSearchResultItem } from "../MemberSearchResultItem";
import {
  EmptyState,
  List,
  LoadingState,
  MenuPanel,
} from "./MemberSearchDropdown.styles";
import type { MemberSearchDropdownProps } from "./MemberSearchDropdown.types";

/** px, converted to rem by `List`'s $maxHeight interpolation. */
const INLINE_LIST_MAX_HEIGHT = 200;

const EMAIL_INVITE_HINT = "Not in your workspace — click to send an invitation";

export const MemberSearchDropdown: React.FC<MemberSearchDropdownProps> = ({
  open,
  isLoading,
  query,
  results,
  emailInviteCandidate,
  menuRef,
  menuRect = null,
  placement = "portal",
  onSelectUser,
  onSelectEmailInvite,
}) => {
  if (!open) return null;
  if (placement === "portal" && !menuRect) return null;

  const showEmpty =
    !isLoading &&
    results.length === 0 &&
    !emailInviteCandidate &&
    query.trim().length >= MIN_WORKSPACE_USER_SEARCH_LENGTH;

  const listMaxHeight =
    placement === "inline"
      ? INLINE_LIST_MAX_HEIGHT
      : menuRect?.maxHeight;

  const panel = (
    <MenuPanel
      ref={menuRef}
      role="listbox"
      aria-label="Member search results"
      $inline={placement === "inline"}
      style={
        placement === "portal" && menuRect
          ? {
              ...(menuRect.bottom != null
                ? { bottom: menuRect.bottom, top: "auto" }
                : { top: menuRect.top, bottom: "auto" }),
              left: menuRect.left,
              width: menuRect.width,
            }
          : undefined
      }
    >
      <List $maxHeight={listMaxHeight}>
        {isLoading ? <LoadingState>Searching…</LoadingState> : null}
        {!isLoading &&
          results.map((user) => (
            <MemberSearchResultItem
              key={user.id}
              name={user.name}
              email={user.email}
              onSelect={() => onSelectUser(user)}
            />
          ))}
        {emailInviteCandidate ? (
          <MemberSearchResultItem
            email={emailInviteCandidate}
            variant="email-invite"
            hint={EMAIL_INVITE_HINT}
            onSelect={() => onSelectEmailInvite(emailInviteCandidate)}
          />
        ) : null}
        {showEmpty ? <EmptyState>No users found</EmptyState> : null}
      </List>
    </MenuPanel>
  );

  if (placement === "inline") return panel;

  return createPortal(panel, document.body);
};

export default MemberSearchDropdown;
